/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import { getFunctions, httpsCallable } from "firebase/functions";
import { initializeApp } from "firebase/app";
import { getFirestore, setDoc, Firestore, onSnapshot, collection, getDocs, doc, QueryDocumentSnapshot, deleteDoc, updateDoc, getDoc } from "firebase/firestore";
import { deleteUser, EmailAuthProvider, FacebookAuthProvider, getAuth, GoogleAuthProvider, onAuthStateChanged, reauthenticateWithCredential, reauthenticateWithPopup, User } from "firebase/auth";
import { getStorage, ref, uploadString, deleteObject, getDownloadURL } from "firebase/storage";
import Monster from "model/monster/Monster";
import NpcData from "model/monster/NpcData";
import Spell from "model/stats/Spell";
import AuthService from "./AuthService";
import { prepStorageName } from "utils/ImageUtils";
import { isReservedUsername } from "auth/reservedUsernames";


// Firebase configuration
const firebaseConfig = {
  apiKey:           "AIzaSyBZ0mwOpv-iMru5NOSuJRIPCk4BkPAsqdA",
  authDomain:       "monster-forge.firebaseapp.com",
  projectId:        "monster-forge",
  storageBucket:    "monster-forge.firebasestorage.app",
  messagingSenderId:"308739255556",
  appId:            "1:308739255556:web:e3af6908eeefd4105eb451",
  measurementId:    "G-LYFYW4HCER"
};

// Initialize Firebase app and services
const app = initializeApp(firebaseConfig);
const db: Firestore = getFirestore(app);
export const auth = getAuth(app);
const storage = getStorage(app);
const usernamesInUseByPrefix = new Map<string, string[]>();
const functions = getFunctions();
const checkAndRegisterUsername = httpsCallable(functions, "checkAndRegisterUsername");
const checkUsernameAvailable = httpsCallable(functions, "checkUsernameAvailable");
const approveMonster = httpsCallable(functions, "approveMonster");
const getPublicMonster = httpsCallable(functions, "getPublicMonster");
const getPublicMonsters = httpsCallable(functions, "getPublicMonsters");
const reportMonster = httpsCallable(functions, "reportMonster");
const rejectMonster = httpsCallable(functions, "rejectMonster");
const getModerationQueue = httpsCallable(functions, "getModerationQueue");
const respondToViolation = httpsCallable(functions, "respondToViolation");
const revertMonsterToPrivate = httpsCallable(functions, "revertMonsterToPrivate");
const setDesiredVisibility = httpsCallable(functions, "setDesiredVisibility");
const getMyMonsterViolations = httpsCallable(functions, "getMyMonsterViolations");
const resolveMyViolation = httpsCallable(functions, "resolveMyViolation");
const getMonsterReports = httpsCallable(functions, "getMonsterReports");
const deleteReport = httpsCallable(functions, "deleteReport");

// Firestore Utility
class FirestoreService {
  private currentUser: User | null = null;
  private unsubscribeUserSnapshot: (() => void) | null = null;
  private unsubscribeMonstersSnapshot: (() => void) | null = null;

  constructor() {
    // Watch for auth state changes
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
      if (!user) {
        // User logged out, clear snapshot listener
        this.clearSnapshotListener();
      }
    });
  }

  async deleteUserDataWithAuthentication(user: User, username: string, password: string, setErrors: (message: string) => void): Promise<boolean> {
    if (!user || !user.email) {
      console.error("No user is currently logged in.");
      return false;
    }

    try {
      // Step 1: Reauthenticate the user
      if (password) {
        const credential = EmailAuthProvider.credential(user.email.trim(), password);
        await reauthenticateWithCredential(user, credential);
      } else {
        let provider = null;
        if (user.providerData[0].providerId.toLowerCase() === 'google.com') {
          provider = new GoogleAuthProvider();
        } else if (user.providerData[0].providerId.toLowerCase() === 'facebook.com') {
          provider = new FacebookAuthProvider();
        }
        if (!provider) {
          console.error('no valid provider found to validate for account deletion');
          return false;
        }
        await reauthenticateWithPopup(user, provider);
      }
      console.log("Reauthentication successful.");

      const db = getFirestore();
      // Step 2: Delete user's subcollections (e.g., monsters) and their images
      const monstersCollectionRef = collection(db, "users", user.uid, "monsters");
      const monsterDocs = await getDocs(monstersCollectionRef);
      if (!monsterDocs.empty) {
        console.log(`Deleting ${monsterDocs.docs.length} monsters...`);
        // Delete images and documents in parallel
        await Promise.all(monsterDocs.docs.map(async (docSnap) => {
          // Delete the image first
          await this.deleteMonsterImage(docSnap.id);
          // Then delete the document
          await deleteDoc(docSnap.ref);
        }));
      }
      console.log("Monsters removed.");

      // Step 3: Delete the user documents
      await deleteDoc(doc(db, "users", user.uid));
      console.log("user doc removed.");
      if (username) {
        console.log("removing username.");
        await deleteDoc(doc(db, "usernames", username.toLowerCase()));
        console.log("username removed.");
      }
      
      // Step 4: Delete user from Firebase Authentication
      await deleteUser(user);
      console.log("User account deleted from Firebase Authentication.");
      return true;
    } catch (error) {
      console.error("Error deleting user:", error);
      AuthService.handleError("Deleting user data error", error, setErrors);
      return false;
    }
  };
  
  async updateBirthday(user: User, birthday: Date): Promise<boolean> {
    return this.updateData(user, { birthday });
  }
  async acceptTerm(user: User): Promise<boolean> {
    return this.updateData(user, { acceptedTerms: new Date() });
  }
  async updateData(user: User, data: any): Promise<boolean> {
    const userRef = doc(db, "users", user.uid);
    try {
      await updateDoc(userRef, data);
      console.log(`Updated Firestore with: ${data}`);
      return true;
    } catch (error) {
      console.error(`Error updating data ${data}:`, error);
      return false;
    }
  }
  
  async registerUsername(userId: string, username: string, birthday: Date): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('calling checkAndRegisterUsername');
      const result: any = await checkAndRegisterUsername({ userId, username, birthday });
      console.log(result.data.message);
      return { success: true };
    } catch (error: any) {
      // Firebase callable errors carry the server's HttpsError message on error.message (e.g. the
      // "That username isn't allowed" reserved-name rejection) — return it so the UI can show why.
      console.error("Error:", error.message);
      return { success: false, error: error?.message || 'Could not register the username.' };
    }
  }

  async isUsernameAvailable(username: string): Promise<boolean> {
    try {
      if (!username || username.length < 3) {
          return false;
      }
      // Reserved (brand/authority) names are rejected by the server at registration; reflect that in
      // the availability indicator instead of showing "available" and then failing on submit.
      if (isReservedUsername(username)) {
          return false;
      }
      const prefix = username.substring(0, 3).toLowerCase();
      const namesInUse = usernamesInUseByPrefix.get(prefix);
      if (namesInUse) {
        return !namesInUse.includes(username.toLowerCase());
      }
      console.log('calling checkUsernameAvailable');
      const result: any = await checkUsernameAvailable({ username });
      console.log(result.data.message);
      if (result.data.success) {
        usernamesInUseByPrefix.set(result.data.prefix, result.data.usernames);
        return !result.data.usernames.includes(username.toLowerCase());
      }
      return false;
    } catch (error: any) {
      console.error("Error:", error.message);
      return false;
    }
  }

  watchUserMonsters(getSpellsByKey: () => Map<string, Spell>, onMonstersChanged: (data: Monster[]) => void): void {
    // If no user is logged in, immediately return an empty array
    if (!this.currentUser) {
      console.log("No user is logged in. Returning empty monsters array.");
      onMonstersChanged([]);
      return;
    }

    try {
      // Reference to the monsters subcollection
      const monstersCollectionRef = collection(db, "users", this.currentUser.uid, "monsters");

      // Set up the snapshot listener with error handling
      this.unsubscribeMonstersSnapshot = onSnapshot(
        monstersCollectionRef,
        (snapshot) => {
          try {
            const monsters: Monster[] = [];
            snapshot.docs.forEach((docSnapshot: QueryDocumentSnapshot) => {
              try {
                const jsonObject = docSnapshot.data();

                // Validate the JSON object before processing
                if (!jsonObject) {
                  console.warn(`Empty or invalid document data for document ID: ${docSnapshot.id}`);
                  return; // Skip this document
                }

                try {
                  // Convert the JSON to a Monster object
                  const mon = Monster.convertFromJson(jsonObject);
                  if (mon) {
                    // Process spells if available
                    if (mon.npcData) {
                      try {
                        NpcData.updateSpellsFromKeys(mon.npcData, getSpellsByKey());
                      } catch (spellError) {
                        console.error(`Error updating spells for monster ${mon.name}:`, spellError);
                      }
                    }
                    monsters.push(mon);
                  }
                } catch (parseError) {
                  console.error(`Error parsing monster from document ${docSnapshot.id}:`, parseError);
                }
              } catch (docError) {
                console.error(`Error processing document ${docSnapshot.id}:`, docError);
              }
            });

            // Update with the processed monsters
            onMonstersChanged(monsters);
          } catch (snapshotError) {
            console.error("Error processing monsters snapshot:", snapshotError);
            onMonstersChanged([]); // Return empty array on error
          }
        },
        (error) => {
          console.error("Error watching monster collection:", error);
          onMonstersChanged([]); // Return empty array on error
        }
      );
    } catch (setupError) {
      console.error("Error setting up monsters snapshot listener:", setupError);
      onMonstersChanged([]); // Return empty array on error
    }
  }

  /**
   * Fetch the public community list: every monster that is approved for 'Public' and has no
   * outstanding violations, across all users. Resolved server-side by the getPublicMonsters Cloud
   * Function (a client collection-group query can't express the approval/violation filters). No
   * login required.
   * @param getSpellsByKey Provider for the spell lookup used to rehydrate NPC spell data.
   * @param includeOwn When false (default) the current user's own monsters are excluded.
   * @returns Array of { username, ownerId, monster } for each listed monster.
   */
  async getPublicMonsters(getSpellsByKey: () => Map<string, Spell>, includeOwn = false):
      Promise<{ username: string; ownerId: string; monster: Monster }[]> {
    try {
      const result: any = await getPublicMonsters({});
      const rows: any[] = result?.data?.monsters || [];
      const out: { username: string; ownerId: string; monster: Monster }[] = [];
      for (const row of rows) {
        try {
          if (!includeOwn && row.ownerId && row.ownerId === this.currentUser?.uid) {
            continue;
          }
          const mon = Monster.convertFromJson(row.monster);
          if (!mon) {
            continue;
          }
          if (mon.npcData) {
            try {
              NpcData.updateSpellsFromKeys(mon.npcData, getSpellsByKey());
            } catch (spellError) {
              console.error(`Error updating spells for public monster ${mon.name}:`, spellError);
            }
          }
          // Pin the monster's ownerId to the authoritative value (the server derives it from the
          // document path) so the community identifier 'community_<ownerId>_<name>' is always stable,
          // even for an older doc that didn't store ownerId.
          if (row.ownerId) {
            mon.ownerId = row.ownerId;
          }
          out.push({ username: row.username || '', ownerId: row.ownerId || '', monster: mon });
        } catch (parseError) {
          console.error("Error parsing public monster:", parseError);
        }
      }
      return out;
    } catch (error: any) {
      console.error("Error fetching public monsters:", error.message);
      return [];
    }
  }

  /**
   * Approve another user's monster via the approveMonster Cloud Function: a 'Pending-Shared'
   * becomes 'Shared', a 'Pending-Public' becomes 'Public', and a reported 'Violation' is restored
   * to the level it had requested. Only succeeds when the current user has canApprove == true; the
   * function changes only the visibility field. The server derives the caller from the auth token,
   * so only ownerId/monsterId are sent.
   * @param ownerId   The Firebase UID of the monster's owner.
   * @param monsterId The monster's Firestore document ID (FirestoreService.prepMonsterName(name)).
   * @returns true if the monster was approved, false otherwise.
   */
  async approveMonster(ownerId: string, monsterId: string): Promise<boolean> {
    if (!this.currentUser) {
      console.error("No user is logged in.");
      return false;
    }
    try {
      console.log('calling approveMonster');
      const result: any = await approveMonster({ ownerId, monsterId });
      console.log(result.data.message);
      return !!result.data.success;
    } catch (error: any) {
      console.error("Error approving monster:", error.message);
      return false;
    }
  }

  /**
   * Fetch a single other user's monster for the public, signed-out view, identified by the
   * owner's username and the monster's name. Resolved entirely server-side by the
   * getPublicMonster Cloud Function. Requires no logged-in user. For approver callers the result
   * also indicates whether the monster has unresolved violation reports (so the moderation UI can
   * offer the violation-response flow even for still-Shared/Public monsters).
   * @param username    The owner's username (case-insensitive; from the usernames collection).
   * @param monsterName The monster's display name (e.g. "Sea Hag").
   * @returns { monster, hasViolations } — monster is null if it doesn't exist / isn't viewable.
   */
  async getPublicMonster(username: string, monsterName: string): Promise<{ monster: Monster | null; hasViolations: boolean }> {
    try {
      const result: any = await getPublicMonster({ username, monsterName });
      const jsonObject = result?.data?.monster;
      const hasViolations = !!result?.data?.hasViolations;
      if (!jsonObject) {
        return { monster: null, hasViolations: false };
      }
      return { monster: Monster.convertFromJson(jsonObject) || null, hasViolations };
    } catch (error: any) {
      console.error("Error fetching public monster:", error.message);
      return { monster: null, hasViolations: false };
    }
  }

  /**
   * Report a monster (viewed via its public link). Recorded server-side by the reportMonster Cloud
   * Function into the 'violations' collection. A 'violation' report (inappropriate content) bumps
   * the monster's open-violation count and hides it from public view until a moderator resolves it;
   * an 'error' report (incorrect data / typo) is recorded as feedback only and never affects
   * visibility. Requires no logged-in user.
   * @returns true if the report was recorded, false otherwise.
   */
  async reportMonster(report: {
    username: string;
    monsterName: string;
    reporterName: string;
    reporterEmail: string;
    reportType: 'violation' | 'error' | 'copyright';
    offensiveType?: 'text' | 'image' | 'both'; // sent for 'violation' / 'copyright' reports
    description: string;
    url: string;
    monsterKey?: string;    // catalog monsters: their data-model key, for the moderation link back
    monsterSource?: string; // catalog monsters: their document source (shown in the moderation queue)
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const result: any = await reportMonster(report);
      return { success: !!result.data.success };
    } catch (error: any) {
      // Surface the server's HttpsError message (e.g. the rate-limit "reporting too quickly" text)
      // so the modal can tell the reporter the specific reason instead of a generic failure.
      console.error("Error reporting monster:", error.message);
      return { success: false, error: error?.message || 'Could not submit your report.' };
    }
  }

  /**
   * Reject another user's monster (approver moderation action). Returns it to 'Private' and
   * records the reason. Only succeeds when the caller has canApprove == true.
   * @returns true if the monster was rejected, false otherwise.
   */
  async rejectMonster(ownerId: string, monsterId: string, reason: string): Promise<boolean> {
    try {
      const result: any = await rejectMonster({ ownerId, monsterId, reason });
      return !!result.data.success;
    } catch (error: any) {
      console.error("Error rejecting monster:", error.message);
      return false;
    }
  }

  /**
   * Respond to a reported ('Violation') monster as an approver. action: 'dismiss' (restore the
   * monster and mark reports resolved), 'confirm' (set the monster to Private and mark reports
   * resolved), or 'delete' (delete the monster and mark reports resolved). Only succeeds when the
   * caller has canApprove == true.
   * @returns true on success, false otherwise.
   */
  async respondToViolation(ownerId: string, monsterId: string, action: 'dismiss' | 'confirm' | 'delete', violationId?: string): Promise<boolean> {
    try {
      const result: any = await respondToViolation({ ownerId, monsterId, action, violationId });
      return !!result.data.success;
    } catch (error: any) {
      console.error("Error responding to violation:", error.message);
      return false;
    }
  }

  /**
   * Take one of the current user's own monsters back to 'Private' (clearing approval and resolving
   * any outstanding violations) so it can be edited. The only way to clear a moderation block,
   * since the owner can't write the moderator-only fields directly.
   * @returns true on success, false otherwise.
   */
  async revertMonsterToPrivate(monsterId: string): Promise<boolean> {
    try {
      const result: any = await revertMonsterToPrivate({ monsterId });
      return !!result.data.success;
    } catch (error: any) {
      console.error("Error setting monster to private:", error.message);
      return false;
    }
  }

  /**
   * Switch one of the current user's own ALREADY-SHARED monsters between 'Shared' and 'Public'
   * without rewriting the moderator-managed fields. Done server-side (Admin SDK) so the client never
   * has to round-trip the approval's serverTimestamp, which a direct write can't reproduce (and which
   * would otherwise be rejected by the security rules). A 'Public' approval already covers 'Shared',
   * so downgrading needs no re-approval. Use revertMonsterToPrivate to go Private. The server also
   * scans the monster for profanity before sharing and rejects it if found.
   * @returns { success, message } — message carries the rejection reason (e.g. profanity) on failure.
   */
  async setDesiredVisibility(monsterId: string, desiredVisibility: 'Shared' | 'Public'): Promise<{ success: boolean; message: string }> {
    try {
      const result: any = await setDesiredVisibility({ monsterId, desiredVisibility });
      return { success: !!result.data.success, message: result.data.message || '' };
    } catch (error: any) {
      // The server's HttpsError message (e.g. a profanity rejection) comes through as error.message.
      console.error("Error updating monster visibility:", error.message);
      return { success: false, message: error.message || 'This monster could not be shared.' };
    }
  }

  /**
   * List the violations recorded against one of the current user's own monsters (reporter identity
   * stripped server-side). Returns source/status/reason/type/date.
   */
  async getMyMonsterViolations(monsterId: string): Promise<any[]> {
    try {
      const result: any = await getMyMonsterViolations({ monsterId });
      return result?.data?.violations || [];
    } catch (error: any) {
      console.error("Error fetching monster violations:", error.message);
      return [];
    }
  }

  /**
   * Acknowledge ("resolve") a single report filed against one of the current user's own monsters. The
   * server recomputes the monster's violationSummary (visibility-affecting reports) and its
   * errorReportCount (data-error feedback) and returns both.
   * @returns { violationSummary, errorReportCount }, or null on failure.
   */
  async resolveMyViolation(monsterId: string, violationId: string):
      Promise<{ violationSummary: { open: number; confirmed: number }; errorReportCount: number } | null> {
    try {
      const result: any = await resolveMyViolation({ monsterId, violationId });
      return result?.data?.success
        ? { violationSummary: result.data.violationSummary, errorReportCount: result.data.errorReportCount || 0 }
        : null;
    } catch (error: any) {
      console.error("Error resolving violation:", error.message);
      return null;
    }
  }

  /**
   * Fetch the moderation queue (pending monsters + grouped violation reports) for an approver.
   * Only succeeds when the caller has canApprove == true. Throws the underlying callable error
   * (with a Firebase `code`, e.g. 'functions/permission-denied') so callers can distinguish
   * "not authorized" from other failures.
   * @returns { pending, reported } arrays.
   */
  async getModerationQueue(): Promise<{ pending: any[]; reported: any[] }> {
    const result: any = await getModerationQueue({});
    return { pending: result?.data?.pending || [], reported: result?.data?.reported || [] };
  }

  /**
   * Approver view: the reports filed against one monster (owner + monster id), WITH reporter identity,
   * for the moderator's "View reports" modal. Only succeeds for canApprove callers.
   * @returns array of report objects (id, reportType, status, offensiveType, description, reporter*, createdAt).
   */
  async getMonsterReports(ownerId: string, monsterId: string): Promise<any[]> {
    try {
      const result: any = await getMonsterReports({ ownerId, monsterId });
      return result?.data?.reports || [];
    } catch (error: any) {
      console.error("Error fetching monster reports:", error.message);
      return [];
    }
  }

  /**
   * Approver action: permanently delete a single (catalog) report by id. Restricted server-side to
   * owner-less catalog reports.
   * @returns true on success, false otherwise.
   */
  async deleteReport(reportId: string): Promise<boolean> {
    try {
      const result: any = await deleteReport({ reportId });
      return !!result.data.success;
    } catch (error: any) {
      console.error("Error deleting report:", error.message);
      return false;
    }
  }

  /**
   * Register a watcher for changes to the user's data.
   * @param onUserDataChanged Callback to invoke when user data changes.
   */
  watchUserData(onUserDataChanged: (data: Record<string, any> | null) => void): boolean {
    if (!this.currentUser) {
      console.error("No user is logged in. Watcher will not be set.");
      onUserDataChanged(null); // Notify listener of no user
      return false;
    }

    const userRef = doc(db, "users", this.currentUser.uid);

    // Set up Firestore snapshot listener
    this.unsubscribeUserSnapshot = onSnapshot(
      userRef,
      (snapshot) => {
        if (snapshot.exists()) {
          onUserDataChanged(snapshot.data());
        } else {
          onUserDataChanged(null); // No data for the user
        }
      },
      (error) => {
        console.error("Error watching user data:", error);
        onUserDataChanged(null); // Notify listener of error
      }
    );
    return true;
  }

  static prepMonsterName(monsterName: string): string {
    // Only replace truly invalid characters for Firebase document IDs
    // Forward slash is the main invalid character to replace
    return monsterName.trim().replace(/\//g, '_');
  }

  static prepStorageName(monsterName: string): string {
    // Delegates to the shared, firebase-free helper (single source of truth for the
    // monster-name -> Storage file-name mapping, also used by isForeignStorageImage).
    return prepStorageName(monsterName);
  }

  /**
   * Upload a monster image to Firebase Storage and return the download URL.
   * @param monsterName The name of the monster
   * @param imageDataUrl The image data URL (must start with 'data:image/')
   * @returns Promise<string> The download URL for the uploaded image
   */
  async uploadMonsterImage(monsterName: string, imageDataUrl: string): Promise<string> {
    if (!this.currentUser) {
      throw new Error("No user is logged in.");
    }

    if (!imageDataUrl || !imageDataUrl.startsWith('data:image/')) {
      throw new Error("Invalid image data URL");
    }

    const validMonsterName = FirestoreService.prepStorageName(monsterName);

    try {
      // Create a reference to the storage location
      // Path: /users/{userId}/images/{monsterName}.webp
      const imageRef = ref(storage, `users/${this.currentUser.uid}/images/${validMonsterName}.webp`);

      // Upload the image (uploadString handles data URLs automatically)
      const snapshot = await uploadString(imageRef, imageDataUrl, 'data_url');

      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log('Image uploaded successfully:', downloadURL);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  /**
   * Delete a monster image from Firebase Storage.
   * @param monsterName The name of the monster
   */
  async deleteMonsterImage(monsterName: string): Promise<void> {
    if (!this.currentUser) {
      throw new Error("No user is logged in.");
    }

    const validMonsterName = FirestoreService.prepStorageName(monsterName);

    try {
      // Create a reference to the storage location
      const imageRef = ref(storage, `users/${this.currentUser.uid}/images/${validMonsterName}.webp`);

      // Delete the image
      await deleteObject(imageRef);
      console.log('Image deleted successfully');
    } catch (error: any) {
      // If the file doesn't exist, that's okay (error code: storage/object-not-found)
      if (error.code === 'storage/object-not-found') {
        console.log('Image does not exist, nothing to delete');
      } else {
        console.error('Error deleting image:', error);
        throw error;
      }
    }
  }

  async deleteMonsterFromCollection(monsterName: string) {
    if (!this.currentUser) {
      console.error("No user is logged in.");
      return;
    }
    // Make sure the monster name is a valid document ID
    const validMonsterName = FirestoreService.prepMonsterName(monsterName);

    try {
      // Delete the monster image from Storage first
      await this.deleteMonsterImage(monsterName);

      // Reference to the monsters subcollection
      const monstersCollectionRef = collection(db, "users", this.currentUser.uid, "monsters");

      // Delete the monster document
      await deleteDoc(doc(monstersCollectionRef, validMonsterName));
      console.log("Monster deleted successfully.");
    } catch (error) {
      console.error("Error deleting monster data:", error);
    }
  };

  async addOrUpdateMonsterToCollection(monster: Monster, serializeFn: (mon: Monster) => Record<string, any>) {
    if (!this.currentUser) {
      console.error("No user is logged in.");
      return;
    }

    if (!monster || !monster.name) {
      console.error("Invalid monster data");
      return;
    }

    // Make sure the monster name is a valid document ID
    const validMonsterName = FirestoreService.prepMonsterName(monster.name);

    try {
      // Validate user ID first
      if (!this.currentUser.uid) {
        throw new Error("User ID is not available");
      }

      // Stamp the monster with the owner's Firebase ID.
      monster.ownerId = this.currentUser.uid;

      // Reference to the monsters subcollection
      const monstersCollectionRef = collection(db, "users", this.currentUser.uid, "monsters");

      // Serialize monster data before creating document reference
      const monsterData = serializeFn(monster);
      
      // Clean up any undefined values that could cause Firebase errors
      const cleanedData: Record<string, any> = {};
      Object.entries(monsterData).forEach(([key, value]) => {
        if (value !== undefined) {
          cleanedData[key] = value;
        }
      });
      
      // Create document reference and save data
      const monsterDocRef = doc(monstersCollectionRef, validMonsterName);
      // Use merge: false to completely replace the document, ensuring deleted/cleared fields are removed
      await setDoc(monsterDocRef, cleanedData, { merge: false });

      console.log("Monster saved successfully.");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Error saving monster data:", errorMessage);
      alert(`Could not save monster: ${errorMessage}`);
    }
  };

  /**
   * Clear the Firestore snapshot listener.
   */
  clearSnapshotListener(): void {
    if (this.unsubscribeUserSnapshot) {
      this.unsubscribeUserSnapshot();
      this.unsubscribeUserSnapshot = null;
    }
    if (this.unsubscribeMonstersSnapshot) {
      this.unsubscribeMonstersSnapshot();
      this.unsubscribeMonstersSnapshot = null;
    }
  }
}

export const firestoreService = new FirestoreService();